import { Download } from "lucide-react";
import PropTypes from "prop-types";
import Modal from "@components/ui-components/modal";
import StatusChip from "@components/ui-components/status-chip";
import { formatDate } from "@helpers/date";
import { DEBIT_NOTE_STATUS_LABEL, DEBIT_NOTE_STATUS_TONE } from "@src/utility/constants/panel/common";
import DebitNoteDocument from "./debit-note-document";

/**
 * A debit note opened from a list: the document itself, with the role's
 * status actions (brand or Ops) passed in as `actions` for the footer.
 */
const DebitNotePreviewModal = ({ preview, brand, caption, actions }) => {
    const { previewNote: note, lines, totals, invoicesLoading, documentRef, closePreview, downloadPdf } = preview;
    if (!note) return null;

    // A closed note says why it was closed.
    const reason = ["rejected", "reverted"].includes(note.status) && note.reason ? note.reason : null;

    return (
        <Modal
            open
            onClose={closePreview}
            width="xl"
            title={(
                <span className="flex items-center gap-3">
                    {note.debit_note_number}
                    <StatusChip tone={DEBIT_NOTE_STATUS_TONE[note.status] || "neutral"} label={DEBIT_NOTE_STATUS_LABEL[note.status] || note.status} />
                </span>
            )}
            subtitle={[brand?.name, note.payout_period ? formatDate(note.payout_period, "MMMM YYYY") : null, caption, reason].filter(Boolean).join(" · ")}
            footer={(
                <>
                    <button
                        type="button"
                        onClick={downloadPdf}
                        disabled={invoicesLoading}
                        className="mr-auto inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
                        <Download className="h-3.5 w-3.5" />
                        Download PDF
                    </button>
                    {actions}
                </>
            )}>
            <div className="max-h-[65vh] overflow-y-auto rounded-lg border border-gray-200 p-6">
                <DebitNoteDocument
                    ref={documentRef}
                    note={note}
                    brand={brand}
                    lines={lines}
                    totals={totals}
                    loading={invoicesLoading} />
            </div>
        </Modal>
    );
};

DebitNotePreviewModal.propTypes = {
    // The return value of useDebitNotePreview
    preview: PropTypes.object.isRequired,
    brand  : PropTypes.object,
    caption: PropTypes.string,
    actions: PropTypes.node
};

export default DebitNotePreviewModal;
